/**
 * SGIS 총조사 통계 지표 정의 및 단계구분도(choropleth) 색상 스케일
 * 지도 오버레이에서 지역별 통계값을 색상 단계로 표현할 때 사용한다.
 */

import type { CensusSummaryResponse } from '@/lib/api/data-contracts';

/** 색상으로 표현 가능한 숫자형 통계 지표 키 */
export type MetricKey =
  | 'ppltnDnsty'
  | 'totPpltn'
  | 'totFamily'
  | 'corpCnt'
  | 'avgAge'
  | 'totHouse'
  | 'employeeCnt';

export interface MetricDef {
  key: MetricKey;
  label: string;
  unit: string;
  /** 범례/툴팁용 값 포맷 */
  format: (value: number) => string;
}

const intFmt = (v: number) => Math.round(v).toLocaleString('ko-KR');
const decFmt = (v: number) => v.toLocaleString('ko-KR', { maximumFractionDigits: 1 });

/** 노출 지표 정의 (전체 지표) */
export const METRIC_DEFS: MetricDef[] = [
  { key: 'ppltnDnsty', label: '인구밀도', unit: '명/㎢', format: decFmt },
  { key: 'totPpltn', label: '총인구', unit: '명', format: intFmt },
  { key: 'totFamily', label: '총가구', unit: '가구', format: intFmt },
  { key: 'corpCnt', label: '사업체수', unit: '개', format: intFmt },
  { key: 'avgAge', label: '평균연령', unit: '세', format: decFmt },
  { key: 'totHouse', label: '총주택', unit: '호', format: intFmt },
  { key: 'employeeCnt', label: '종사자수', unit: '명', format: intFmt },
];

export function getMetricDef(key: MetricKey): MetricDef {
  return METRIC_DEFS.find((m) => m.key === key) ?? METRIC_DEFS[0];
}

/** 지표값 추출 (결측 시 undefined) */
export function getMetricValue(
  summary: CensusSummaryResponse | undefined,
  key: MetricKey
): number | undefined {
  const v = summary?.[key];
  return typeof v === 'number' ? v : undefined;
}

/** YlOrRd 계열 색상 램프 (낮음 → 높음) */
const COLOR_RAMP = [
  '#ffffcc',
  '#ffeda0',
  '#fed976',
  '#feb24c',
  '#fd8d3c',
  '#fc4e2a',
  '#e31a1c',
  '#b10026',
];

/** 결측/매칭 실패 지역 색상 */
export const NO_DATA_COLOR = '#e5e7eb';

export interface ChoroplethScale {
  /** 버킷 색상 (낮음 → 높음) */
  colors: string[];
  /** 버킷 상한 경계값 (length = colors.length - 1) */
  thresholds: number[];
  min: number;
  max: number;
  /** 값 → 색상 (결측은 NO_DATA_COLOR) */
  colorFor: (value: number | undefined | null) => string;
}

function quantile(sorted: number[], p: number): number {
  if (sorted.length === 1) return sorted[0];
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * 값 배열로부터 분위수(quantile) 기반 색상 스케일을 만든다.
 * - 값이 없으면 null(전체 결측 처리)
 * - 고유값이 적으면 버킷 수를 줄여 단일/소수 색상으로 표현
 */
export function buildChoroplethScale(values: number[], ramp: string[] = COLOR_RAMP): ChoroplethScale | null {
  const nums = values.filter((v) => Number.isFinite(v));
  if (nums.length === 0) return null;

  const sorted = [...nums].sort((a, b) => a - b);
  const uniqueCount = new Set(sorted).size;
  const bucketCount = Math.min(ramp.length, Math.max(1, uniqueCount));
  const colors = ramp.slice(ramp.length - bucketCount);

  const thresholds: number[] = [];
  for (let i = 1; i < bucketCount; i++) {
    thresholds.push(quantile(sorted, i / bucketCount));
  }

  const colorFor = (value: number | undefined | null): string => {
    if (value === undefined || value === null || !Number.isFinite(value)) {
      return NO_DATA_COLOR;
    }
    let idx = thresholds.findIndex((t) => value <= t);
    if (idx === -1) idx = colors.length - 1;
    return colors[idx];
  };

  return {
    colors,
    thresholds,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    colorFor,
  };
}
